import axios, {isAxiosError} from 'axios';
import {useAuthStore} from '../stores/authStore';
import {ZodTypeAny} from "zod";
import type {Method} from 'axios';

export class ApiError extends Error {
  readonly serverCode?: string;

  constructor(message: string, serverCode?: string) {
    super(message);
    this.name = 'ApiError';
    this.serverCode = serverCode;
  }
}

const isDev = import.meta.env.MODE === 'development' || import.meta.env.NODE_ENV === 'development';
const ENABLE_STRICT_VALIDATION = isDev && (import.meta.env.VITE_STRICT_API_VALIDATION !== 'false');

// Axios-style helper for typed/validated JSON requests
export type RequestJsonOptions = {
  url: string;
  method?: Method;
  data?: unknown;
  headers?: Record<string, string>;
  schema?: ZodTypeAny;
};

export async function requestJson<T>(options: RequestJsonOptions): Promise<T> {
  const url = options.url;
  const method = (options.method || 'GET') as Method;
  const headers = options.headers;
  const dataPayload = options.data;
  const schema = options.schema;

  const res = await api.request({
    url,
    method,
    headers,
    data: dataPayload,
    withCredentials: true,
  });

  const data = res.data;

  if (schema) {
    if (ENABLE_STRICT_VALIDATION) {
      const parsed = schema.safeParse(data);
      if (!parsed.success) {
        // Log rich details and fail fast in dev
        console.error('Response validation failed', parsed.error.issues, { data });
        throw new Error('Response validation failed');
      }
      return parsed.data as T;
    } else {
      // Production path: try-parse to narrow when possible, fall back to raw data
      const parsed = schema.safeParse(data);
      return (parsed.success ? parsed.data : data) as T;
    }
  }

  return data as T;
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - attach session token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().sessionToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Session expired or invalid
      useAuthStore.getState().clearAuth();
    }

    // If the server sent `{ ok:false, message: string, code?: string }`, wrap in ApiError
    if (isAxiosError(error)) {
      const data = error.response?.data as { message?: unknown; code?: unknown } | undefined;
      const serverMessage = data?.message;
      if (serverMessage && typeof serverMessage === 'string') {
        const serverCode = data?.code;
        return Promise.reject(new ApiError(
          serverMessage,
          typeof serverCode === 'string' ? serverCode : undefined,
        ));
      }
    }

    return Promise.reject(error);
  }
);

export default api;
